import { DraggableSyntheticListeners } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { createContext, FC, useContext } from 'react';
import styled, { css } from 'styled-components';

const DndWrapper = styled.div<{ isDragging?: boolean }>`
  height: 100%;
  ${({ isDragging }) =>
    isDragging
      ? css`
          opacity: 0;
        `
      : ''};
`;

interface ChartDndContext {
  listeners: DraggableSyntheticListeners | null;
  attributes: {
    role: string;
    tabIndex: number;
    'aria-pressed': boolean | undefined;
    'aria-roledescription': string;
    'aria-describedby': string;
  } | null;
}

const ChartContext = createContext<ChartDndContext | null>(null);

export const ChartDndContextProvider: FC<ChartDndContext> = ({
  listeners,
  attributes,
  children,
}) => {
  return (
    <ChartContext.Provider value={{ listeners, attributes }}>
      {children}
    </ChartContext.Provider>
  );
};

export const useChartDndContext = (): ChartDndContext => {
  const chartContext = useContext(ChartContext);

  if (!chartContext) {
    return {
      listeners: null,
      attributes: null,
    };
  }

  return chartContext;
};

export const DraggableComponent = ({
  Component,
  dashboardFilter,
  dashboardSimpleFilter,
  widget,
  id,
}) => {
  const {
    isDragging,
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition: transition || undefined,
    backgroundColor: 'var(--blue-10)',
    outline: isDragging ? '2px dashed var(--blue-60)' : 'none',
    height: '100%',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ChartDndContextProvider attributes={attributes} listeners={listeners}>
        <DndWrapper isDragging={isDragging}>
          <Component
            widget={widget}
            dashboardFilter={dashboardFilter}
            dashboardSimpleFilter={dashboardSimpleFilter}
          />
        </DndWrapper>
      </ChartDndContextProvider>
    </div>
  );
};
